'use client';

import { PanelLeftDashedIcon, PanelLeftIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';
import { Button } from '../../../button';
import { useDesignSystemIntl } from '../../../hooks/use-design-system-intl';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../tooltip';

import { usePanelContext } from './panel-context';
import {
  PANEL_KEYBOARD_SHORTCUT,
  PANEL_PINNED_ID,
  type PanelTriggerProps,
} from './panel.types';

/**
 * Panel trigger button for toggling pinned panel state.
 * Uses the same icon as SidebarTrigger (PanelLeftIcon on desktop, MenuIcon on mobile).
 * Only renders when panel content is available for the current page.
 *
 * @example
 * ```tsx
 * <PageShell.Header>
 *   <PanelTrigger />
 * </PageShell.Header>
 * ```
 */

const PanelTrigger = React.forwardRef<HTMLButtonElement, PanelTriggerProps>(
  ({ 'data-testid': testId, className, onClick, label: labelProp, ...props }, ref) => {
    const { isPinned, setIsPinned, hasPanelContent } = usePanelContext();
    const intl = useDesignSystemIntl();
    const label =
      labelProp ??
      (isPinned
        ? intl.formatMessage({
            defaultMessage: 'Unpin panel',
            id: '41pQmxRMX7',
            description:
              'Tooltip and accessible label for the page shell panel button while the panel is pinned open; unpinning lets it hide automatically',
          })
        : intl.formatMessage({
            defaultMessage: 'Pin panel',
            id: 'H7PjvDNcXF',
            description:
              'Tooltip and accessible label for the page shell panel button while the panel hides automatically; pinning keeps it open',
          }));

    // Toggle panel pinned state
    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        setIsPinned(!isPinned);
        onClick?.(event);
      },
      [isPinned, setIsPinned, onClick]
    );

    // Only render when current page has panel content
    if (!hasPanelContent) {
      return null;
    }

    // Platform-aware modifier key: ⌘ for Mac, Ctrl+ for others
    // Uses userAgent as platform is deprecated
    const isMac =
      typeof navigator !== 'undefined' &&
      /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
    const modifierKey = isMac ? '⌘' : 'Ctrl+';
    const ariaLabel = `${label} (${modifierKey}${PANEL_KEYBOARD_SHORTCUT.toUpperCase()})`;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              ref={ref}
              data-testid={testId}
              data-slot="panel-trigger"
              variant="ghost"
              size="sm"
              iconOnly
              onClick={handleClick}
              className={cn('size-7', className)}
              aria-label={ariaLabel}
              aria-expanded={isPinned}
              aria-controls={PANEL_PINNED_ID}
              {...props}
            >
              {isPinned ? <PanelLeftDashedIcon /> : <PanelLeftIcon />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
);
PanelTrigger.displayName = 'PanelTrigger';

export { PanelTrigger };
